<template>
  <head>
    <meta charset="utf-8"/>
    <title>Desk Birdy | Revenue Management Dashboard</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Revenue management system by Nouveta LTD" name="description"/>
    <meta content="Kelvin Njuguna" name="author"/>
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- Leaflet CSS for maps -->
    <link rel="stylesheet" href="https://unpkg.com/leaflet@1.7.1/dist/leaflet.css"/>

    <!-- DataTables -->
    <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css"/>
    <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css"/>

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css"/>
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css"/>
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css"/>
  </head>

  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <NavigationBar/>

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">{{ getPageTitle() }}</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="index.html">Dashboards</a></li>
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Revenue</a></li>
                    <li class="breadcrumb-item active">{{ selectedStream.name }}</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
          <!-- end page title -->

          <!-- Revenue Stream & Date Range Selector -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card border-primary">
                <div class="card-body py-3">
                  <div class="row align-items-center">
                    <!-- Stream Selector -->
                    <div class="col-md-3">
                      <label class="form-label fw-bold">Revenue Stream</label>
                      <select class="form-select form-select-lg" v-model="selectedStreamId" @change="onStreamChange">
                        <option value="ALL">All Revenue Streams</option>
                        <option v-for="stream in availableStreams" :key="stream.id" :value="stream.incomeTypePrefix">
                          {{ stream.name }} ({{ stream.incomeTypePrefix }})
                        </option>
                      </select>
                    </div>

                    <!-- Date Range Selector -->
                    <div class="col-md-3" v-if="selectedStreamId">
                      <label class="form-label fw-bold">Date Range</label>
                      <select class="form-select form-select-lg" v-model="dateRange.type" @change="onDateRangeChange">
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="this_week">This Week</option>
                        <option value="this_month">This Month</option>
                        <option value="custom">Custom Range</option>
                      </select>
                    </div>

                    <!-- Custom Date Range Inputs -->
                    <div class="col-md-2" v-if="selectedStreamId && dateRange.type === 'custom'">
                      <label class="form-label fw-bold">Start Date</label>
                      <input type="date" class="form-control" v-model="dateRange.startDate" @change="fetchData">
                    </div>
                    <div class="col-md-2" v-if="selectedStreamId && dateRange.type === 'custom'">
                      <label class="form-label fw-bold">End Date</label>
                      <input type="date" class="form-control" v-model="dateRange.endDate" @change="fetchData">
                    </div>
                    <div class="col-md-2" v-if="selectedStreamId && dateRange.type === 'custom'">
                      <label class="form-label fw-bold">&nbsp;</label>
                      <button class="btn btn-primary d-block" @click="fetchData" :disabled="loading">
                        <i class="bx bx-search"></i> Apply
                      </button>
                    </div>

                    <!-- Stream Info (when not custom) -->
                    <div class="col-md-3" v-if="selectedStreamId && dateRange.type !== 'custom'">
                      <div class="text-center">
                        <h6 class="text-muted mb-1">Monthly Target</h6>
                        <h4 class="text-primary mb-0">KES {{ numberFormat(selectedStream.monthlyTarget) }}</h4>
                      </div>
                    </div>
                    <div class="col-md-3" v-if="selectedStreamId && dateRange.type !== 'custom'">
                      <div class="text-center">
                        <h6 class="text-muted mb-1">Current Period</h6>
                        <span class="badge bg-info font-size-12">{{ summary.dateRange?.label || 'Current Period' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <MarketsToolBar v-if="selectedStreamId" :selected-stream="selectedStream" :date-range="dateRange"/>

          <!-- Show content always since we default to ALL -->
          <div v-if="!selectedStreamId" class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body text-center py-5">
                  <div class="avatar-lg mx-auto mb-4">
                    <div class="avatar-title bg-primary-subtle text-primary rounded-circle font-size-24">
                      <i class="bx bx-bar-chart-alt-2"></i>
                    </div>
                  </div>
                  <h4 class="text-muted">Loading Revenue Data</h4>
                  <p class="text-muted">Fetching all revenue streams data...</p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedStreamId">
            <!-- Data Status Bar -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <span v-if="lastUpdated" class="text-muted small">
                      <i class="bx bx-time-five"></i> Last updated: {{ formatLastUpdated(lastUpdated) }}
                    </span>
                    <span class="text-muted small ms-3" v-if="summary.dateRange">
                      <i class="bx bx-calendar"></i> {{ summary.dateRange.label }}
                    </span>
                  </div>
                  <div>
                    <transition name="fade">
                      <span v-if="loading" class="badge bg-info text-white">
                        <i class="bx bx-loader-alt bx-spin me-1"></i> Refreshing data...
                      </span>
                    </transition>
                  </div>
                </div>
              </div>
            </div>

            <!-- Date Range Quick Actions -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-body py-2">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="btn-group btn-group-sm" role="group">
                        <button
                            type="button"
                            class="btn"
                            :class="dateRange.type === 'today' ? 'btn-primary' : 'btn-outline-primary'"
                            @click="setDateRange('today')"
                        >
                          Today
                        </button>
                        <button
                            type="button"
                            class="btn"
                            :class="dateRange.type === 'yesterday' ? 'btn-primary' : 'btn-outline-primary'"
                            @click="setDateRange('yesterday')"
                        >
                          Yesterday
                        </button>
                        <button
                            type="button"
                            class="btn"
                            :class="dateRange.type === 'this_week' ? 'btn-primary' : 'btn-outline-primary'"
                            @click="setDateRange('this_week')"
                        >
                          This Week
                        </button>
                        <button
                            type="button"
                            class="btn"
                            :class="dateRange.type === 'this_month' ? 'btn-primary' : 'btn-outline-primary'"
                            @click="setDateRange('this_month')"
                        >
                          This Month
                        </button>
                      </div>

                      <div class="d-flex align-items-center">
                        <div class="form-check form-switch me-3">
                          <input class="form-check-input" type="checkbox" v-model="autoRefresh" >
                          <label class="form-check-label small">
                            Auto-refresh
                          </label>
                        </div>
                        <button class="btn btn-sm btn-success me-2" @click="exportData">
                          <i class="bx bx-download"></i> Export
                        </button>
                        <button class="btn btn-sm btn-primary" @click="fetchData" :disabled="loading">
                          <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Executive Summary Cards -->
            <div class="row" :class="{ 'loading-cards': loading && !hasDataLoaded }">
              <div class="col-md-3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">Total Revenue</p>
                        <h4 class="mb-0" v-if="!loading || hasDataLoaded">KES {{numberFormat(summary.totalRevenue)}}</h4>
                        <div v-else class="skeleton-loader mb-2"></div>
                        <p class="text-success mb-0 mt-2" v-if="!loading || hasDataLoaded">
                          <i class="mdi mdi-arrow-up"></i> {{summary.growthRate}}% Growth
                        </p>
                        <div v-else class="skeleton-loader skeleton-sm"></div>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-primary">
                          <span class="avatar-title">
                            <i class="bx bx-money font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">Target Achievement</p>
                        <h4 class="mb-0" v-if="!loading || hasDataLoaded">{{summary.achievementRate}}%</h4>
                        <div v-else class="skeleton-loader mb-2"></div>
                        <p class="text-muted mb-0 mt-2" v-if="!loading || hasDataLoaded">
                          of KES {{numberFormat(summary.yearlyTarget)}}
                        </p>
                        <div v-else class="skeleton-loader skeleton-sm"></div>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-success">
                          <span class="avatar-title">
                            <i class="bx bx-target-lock font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">Active {{ getZoneLabel() }}</p>
                        <h4 class="mb-0" v-if="!loading || hasDataLoaded">{{summary.activeZones}}</h4>
                        <div v-else class="skeleton-loader mb-2"></div>
                        <p class="text-muted mb-0 mt-2" v-if="!loading || hasDataLoaded">
                          {{summary.activeUsers}} Active Users
                        </p>
                        <div v-else class="skeleton-loader skeleton-sm"></div>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-warning">
                          <span class="avatar-title">
                            <i :class="getStreamIcon()" class="font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">Total Transactions</p>
                        <h4 class="mb-0" v-if="!loading || hasDataLoaded">{{numberFormat(summary.totalTransactions)}}</h4>
                        <div v-else class="skeleton-loader mb-2"></div>
                        <p class="text-muted mb-0 mt-2" v-if="!loading || hasDataLoaded">
                          Avg: KES {{numberFormat(transactionMetrics.averageTransactionValue)}}
                        </p>
                        <div v-else class="skeleton-loader skeleton-sm"></div>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-info">
                          <span class="avatar-title">
                            <i class="bx bx-receipt font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters Row -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-3">
                        <div class="form-group">
                          <label>Sub County</label>
                          <select class="form-select" v-model="filters.subCountyID" @change="fetchData">
                            <option value="">All Sub Counties</option>
                            <option v-for="sc in subCounties" :key="sc.subCountyID" :value="sc.subCountyID">
                              {{sc.subCountyName}}
                            </option>
                          </select>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="form-group">
                          <label>Ward</label>
                          <select class="form-select" v-model="filters.wardID" @change="fetchData">
                            <option value="">All Wards</option>
                            <option v-for="ward in wards" :key="ward.wardID" :value="ward.wardID">
                              {{ward.wardName}}
                            </option>
                          </select>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="form-group">
                          <label>{{ getZoneLabel() }}</label>
                          <select class="form-select" v-model="filters.zone" @change="fetchData">
                            <option value="">All {{ getZoneLabel() }}</option>
                            <option v-for="zone in zones" :key="zone.zone" :value="zone.zone">
                              {{zone.zone}}
                            </option>
                          </select>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="form-group">
                          <label>Period Comparison</label>
                          <div class="text-muted small mt-2">
                            vs {{ getPreviousPeriodLabel() }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Content Row -->
            <div class="row">
              <!-- Revenue Performance Chart -->
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-header bg-white">
                    <h4 class="card-title">Revenue Performance - {{ summary.dateRange?.label || 'Current Period' }}</h4>
                    <p class="text-muted mb-0">Performance comparison with previous period</p>
                  </div>
                  <div class="card-body">
                    <div id="revenuePerformanceChart" style="height: 400px;"></div>
                  </div>
                </div>
              </div>

              <!-- Performance Insights Panel -->
              <div class="col-lg-4">
                <div class="card border-info">
                  <div class="card-header bg-info-subtle d-flex justify-content-between align-items-center">
                    <div>
                      <h4 class="card-title mb-0 text-info">
                        <i class="bx bx-trending-up me-2"></i>Performance Insights
                      </h4>
                      <p class="text-muted mb-0">Real-time analytics & trends</p>
                    </div>
                    <div>
                      <button class="btn btn-sm btn-outline-info" @click="refreshInsights" :disabled="loadingInsights">
                        <i class="bx bx-refresh" :class="{ 'bx-spin': loadingInsights }"></i>
                      </button>
                    </div>
                  </div>
                  <div class="card-body">
                    <!-- Loading State -->
                    <div v-if="loadingInsights" class="text-center py-4">
                      <div class="spinner-border spinner-border-sm text-info" role="status">
                        <span class="visually-hidden">Loading insights...</span>
                      </div>
                      <p class="text-muted mt-2 mb-0">Analyzing performance...</p>
                    </div>

                    <!-- Performance Insights Content -->
                    <div v-else class="insights-content">
                      <!-- Peak Hour -->
                      <div class="insight-item mb-3" v-if="performanceInsights.peakHour?.hour">
                        <div class="d-flex align-items-center mb-2">
                          <div class="insight-icon bg-warning text-dark rounded-circle me-3">
                            <i class="bx bx-time-five"></i>
                          </div>
                          <div class="flex-grow-1">
                            <h6 class="mb-0">Peak Hour</h6>
                            <span class="text-muted small">Highest activity period</span>
                          </div>
                        </div>
                        <div class="ps-5">
                          <div class="fw-bold text-warning">{{ performanceInsights.peakHour.hour }}</div>
                          <div class="small text-muted">
                            KES {{ numberFormat(performanceInsights.peakHour.revenue) }}
                            • {{ performanceInsights.peakHour.transactions }} transactions
                          </div>
                        </div>
                      </div>

                      <!-- Top Collector -->
                      <div class="insight-item mb-3" v-if="performanceInsights.topCollector?.name">
                        <div class="d-flex align-items-center mb-2">
                          <div class="insight-icon bg-success text-white rounded-circle me-3">
                            <i class="bx bx-trophy"></i>
                          </div>
                          <div class="flex-grow-1">
                            <h6 class="mb-0">Top Collector</h6>
                            <span class="text-muted small">Leading performer</span>
                          </div>
                        </div>
                        <div class="ps-5">
                          <div class="fw-bold text-success">{{ performanceInsights.topCollector.name }}</div>
                          <div class="small text-muted">
                            KES {{ numberFormat(performanceInsights.topCollector.revenue) }}
                            <span v-if="performanceInsights.topCollector.zone"> • {{ performanceInsights.topCollector.zone }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- Fastest Growing Zone -->
                      <div class="insight-item mb-3" v-if="performanceInsights.fastestGrowing?.zone">
                        <div class="d-flex align-items-center mb-2">
                          <div class="insight-icon bg-primary text-white rounded-circle me-3">
                            <i class="bx bx-trending-up"></i>
                          </div>
                          <div class="flex-grow-1">
                            <h6 class="mb-0">Fastest Growing</h6>
                            <span class="text-muted small">{{ getZoneLabel() }}</span>
                          </div>
                        </div>
                        <div class="ps-5">
                          <div class="fw-bold text-primary">{{ performanceInsights.fastestGrowing.zone }}</div>
                          <div class="small text-muted">
                            <span class="text-success">
                              <i class="bx bx-up-arrow-alt"></i> +{{ performanceInsights.fastestGrowing.growth }}% growth
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Revenue Velocity -->
                      <div class="insight-item mb-3" v-if="performanceInsights.revenueVelocity?.perHour">
                        <div class="d-flex align-items-center mb-2">
                          <div class="insight-icon bg-info text-white rounded-circle me-3">
                            <i class="bx bx-tachometer"></i>
                          </div>
                          <div class="flex-grow-1">
                            <h6 class="mb-0">Revenue Velocity</h6>
                            <span class="text-muted small">Current rate</span>
                          </div>
                        </div>
                        <div class="ps-5">
                          <div class="fw-bold text-info">KES {{ numberFormat(performanceInsights.revenueVelocity.perHour) }}/hour</div>
                          <div class="small text-muted">
                            Projected: KES {{ numberFormat(performanceInsights.revenueVelocity.projected) }}
                          </div>
                        </div>
                      </div>

                      <!-- Efficiency Score -->
                      <div class="insight-item mb-0" v-if="performanceInsights.efficiency?.score !== undefined">
                        <div class="d-flex align-items-center mb-2">
                          <div class="insight-icon bg-secondary text-white rounded-circle me-3">
                            <i class="bx bx-stats"></i>
                          </div>
                          <div class="flex-grow-1">
                            <h6 class="mb-0">Efficiency Score</h6>
                            <span class="text-muted small">System performance</span>
                          </div>
                        </div>
                        <div class="ps-5">
                          <div class="d-flex align-items-center mb-2">
                            <div class="flex-grow-1">
                              <div class="progress" style="height: 8px;">
                                <div
                                    class="progress-bar"
                                    :class="getEfficiencyProgressClass(performanceInsights.efficiency.score)"
                                    :style="{ width: performanceInsights.efficiency.score + '%' }"
                                ></div>
                              </div>
                            </div>
                            <span
                                class="badge ms-2"
                                :class="getEfficiencyBadgeClass(performanceInsights.efficiency.score)"
                            >
                              {{ performanceInsights.efficiency.score }}%
                            </span>
                          </div>
                          <div class="small text-muted">
                            {{ performanceInsights.efficiency.activeZones }}/{{ performanceInsights.efficiency.totalZones }} zones active
                            • {{ getEfficiencyLabel(performanceInsights.efficiency.score) }}
                          </div>
                        </div>
                      </div>

                      <!-- Quick Actions -->
                      <div class="mt-4 pt-3 border-top">
                        <div class="d-flex gap-2">
                          <button class="btn btn-sm btn-outline-info flex-fill" @click="viewDetailedAnalytics">
                            <i class="bx bx-line-chart"></i> Detailed Analytics
                          </button>
                          <button class="btn btn-sm btn-outline-success flex-fill" @click="exportPerformanceInsights">
                            <i class="bx bx-download"></i> Export Report
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- No Data State -->
                    <div v-if="!loadingInsights && !performanceInsights.peakHour && !performanceInsights.topCollector" class="text-center py-4">
                      <div class="text-muted">
                        <i class="bx bx-info-circle font-size-24"></i>
                        <p class="mt-2 mb-0">No insights available</p>
                        <small>Insights will appear as data becomes available</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Performance Analysis Row -->
            <div class="row">
              <!-- Top Users Performance -->
              <div class="col-lg-6">
                <div class="card border-success">
                  <div class="card-header bg-success-subtle d-flex justify-content-between align-items-center">
                    <div>
                      <h4 class="card-title mb-0 text-success">
                        <i class="bx bx-trophy me-2"></i>Top Revenue Collectors
                      </h4>
                      <p class="text-muted mb-0">Excellence in Performance</p>
                    </div>
                    <div>
                      <span class="badge bg-success me-2">{{ userPerformance.topPerformers?.length || 0 }} performers</span>
                      <button class="btn btn-sm btn-outline-success" @click="showAllUsersModal = true">
                        <i class="bx bx-list-ul"></i> View All
                      </button>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Name</th>
                          <th>Zone</th>
                          <th class="text-end">Revenue</th>
                          <th class="text-end">Achievement</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="user in userPerformance.topPerformers"
                            :key="user.idNo"
                            @click="viewUserDetails(user.idNo)"
                            style="cursor: pointer;"
                            class="table-success-subtle">
                          <td>
                            <span class="badge" :class="user.rank <= 3 ? 'bg-warning' : 'bg-success'">
                              <i v-if="user.rank === 1" class="bx bx-medal"></i>
                              <i v-else-if="user.rank === 2" class="bx bx-medal"></i>
                              <i v-else-if="user.rank === 3" class="bx bx-medal"></i>
                              #{{ user.rank }}
                            </span>
                          </td>
                          <td>
                            <div>
                              <div class="fw-bold">{{user.names}}</div>
                              <small class="text-muted">ID: {{user.idNo}}</small>
                            </div>
                          </td>
                          <td>{{user.zone || 'Unassigned'}}</td>
                          <td class="text-end">
                            <span class="fw-bold text-success">{{numberFormat(user.totalRevenue)}}</span>
                          </td>
                          <td class="text-end">
                            <span class="badge bg-success">
                              {{user.achievementRate}}%
                            </span>
                          </td>
                        </tr>
                        <tr v-if="!userPerformance.topPerformers || userPerformance.topPerformers.length === 0">
                          <td colspan="5" class="text-center text-muted py-3">
                            <i class="bx bx-info-circle"></i> No performance data available
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Bottom Revenue Collectors -->
              <div class="col-lg-6">
                <div class="card border-danger">
                  <div class="card-header bg-danger-subtle d-flex justify-content-between align-items-center">
                    <div>
                      <h4 class="card-title mb-0 text-danger">
                        <i class="bx bx-error-circle me-2"></i>Bottom Revenue Collectors
                      </h4>
                      <p class="text-muted mb-0">Performance Issues</p>
                    </div>
                    <div>
                      <span class="badge bg-danger me-2">{{ bottomPerformers.length }} users</span>
                      <button class="btn btn-sm btn-outline-danger" @click="showAllUsersModal = true">
                        <i class="bx bx-list-ul"></i> View All
                      </button>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Name</th>
                          <th>Zone</th>
                          <th class="text-end">Revenue</th>
                          <th class="text-end">Achievement</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="user in bottomPerformers"
                            :key="user.idNo"
                            @click="viewUserDetails(user.idNo)"
                            style="cursor: pointer;"
                            class="table-danger-subtle">
                          <td>
                            <span class="badge bg-secondary">
                              #{{ user.rank }}
                            </span>
                          </td>
                          <td>
                            <div>
                              <div class="fw-bold">{{user.names}}</div>
                              <small class="text-muted">ID: {{user.idNo}}</small>
                            </div>
                          </td>
                          <td>{{user.zone || 'Unassigned'}}</td>
                          <td class="text-end">
                            <span class="fw-bold text-danger">{{numberFormat(user.totalRevenue)}}</span>
                          </td>
                          <td class="text-end">
                            <span class="badge bg-danger">
                              {{user.achievementRate}}%
                            </span>
                          </td>
                        </tr>
                        <tr v-if="bottomPerformers.length === 0">
                          <td colspan="5" class="text-center text-muted py-3">
                            <i class="bx bx-check-circle text-success"></i> All collectors performing above minimum threshold
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Quick Actions for Bottom Performers -->
                    <div class="mt-3" v-if="bottomPerformers.length > 0">
                      <div class="alert alert-warning alert-dismissible fade show" role="alert">
                        <h6 class="alert-heading">
                          <i class="bx bx-info-circle"></i> Action Required
                        </h6>
                        <p class="mb-0">
                          {{ bottomPerformers.length }} collector(s) need attention.
                          Consider providing additional training or support.
                        </p>
                        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Market Performance Analysis Row -->
            <div class="row">
              <!-- Top Performing Markets (Moved here for better organization) -->
              <div class="col-lg-6">
                <div class="card border-primary">
                  <div class="card-header bg-primary-subtle d-flex justify-content-between align-items-center">
                    <div>
                      <h4 class="card-title mb-0 text-primary">
                        <i class="bx bx-trending-up me-2"></i>Top Performing {{ getZoneLabel() }}
                      </h4>
                      <p class="text-muted mb-0">Highest Revenue Generators</p>
                    </div>
                    <div>
                      <span class="badge bg-primary me-2">{{ topZones.length }} zones</span>
                      <button class="btn btn-sm btn-outline-primary" @click="openAllMarketsModal" :disabled="loading">
                        <i class="bx bx-list-ul"></i> View All
                      </button>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                        <tr>
                          <th>Rank</th>
                          <th>{{ getZoneLabel() }}</th>
                          <th class="text-end">Revenue</th>
                          <th class="text-end">Growth</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(zone, index) in topZones" :key="zone.zone" class="table-primary-subtle">
                          <td>
                            <span class="badge" :class="index < 3 ? 'bg-warning' : 'bg-primary'">
                              <i v-if="index === 0" class="bx bx-crown"></i>
                              #{{ index + 1 }}
                            </span>
                          </td>
                          <td>
                            <div>
                              <div class="fw-bold">{{zone.zone || 'Unassigned'}}</div>
                              <small class="text-muted">{{zone.userCount}} users • {{zone.transactionCount}} transactions</small>
                            </div>
                          </td>
                          <td class="text-end">
                            <span class="fw-bold text-primary">{{numberFormat(zone.currentRevenue)}}</span>
                          </td>
                          <td class="text-end">
                            <span :class="zone.growth > 0 ? 'text-success' : 'text-danger'">
                              <i :class="zone.growth > 0 ? 'bx bx-up-arrow-alt' : 'bx bx-down-arrow-alt'"></i>
                              {{zone.growth}}%
                            </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Bottom Performing Markets -->
              <div class="col-lg-6">
                <div class="card border-warning">
                  <div class="card-header bg-warning-subtle d-flex justify-content-between align-items-center">
                    <div>
                      <h4 class="card-title mb-0 text-warning">
                        <i class="bx bx-trending-down me-2"></i>Bottom Performing {{ getZoneLabel() }}
                      </h4>
                      <p class="text-muted mb-0">Need Attention</p>
                    </div>
                    <span class="badge bg-warning">{{ bottomZones.length }} zones</span>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                        <tr>
                          <th>Zone</th>
                          <th class="text-end">Revenue</th>
                          <th class="text-end">Growth</th>
                          <th class="text-center">Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="zone in bottomZones" :key="zone.zone" class="table-warning-subtle">
                          <td>
                            <div>
                              <div class="fw-bold">{{zone.zone || 'Unassigned'}}</div>
                              <small class="text-muted">{{zone.userCount}} users</small>
                            </div>
                          </td>
                          <td class="text-end">
                            <span class="fw-bold text-warning">{{numberFormat(zone.currentRevenue)}}</span>
                          </td>
                          <td class="text-end">
                            <span :class="zone.growth >= 0 ? 'text-success' : 'text-danger'">
                              <i :class="zone.growth >= 0 ? 'bx bx-up-arrow-alt' : 'bx bx-down-arrow-alt'"></i>
                              {{zone.growth}}%
                            </span>
                          </td>
                          <td class="text-center">
                            <span v-if="zone.currentRevenue === 0" class="badge bg-danger">Inactive</span>
                            <span v-else-if="zone.growth < -20" class="badge bg-danger">Critical</span>
                            <span v-else-if="zone.growth < 0" class="badge bg-warning">Declining</span>
                            <span v-else class="badge bg-info">Low Revenue</span>
                          </td>
                        </tr>
                        <tr v-if="bottomZones.length === 0">
                          <td colspan="4" class="text-center text-muted py-3">
                            <i class="bx bx-check-circle text-success"></i> All {{ getZoneLabel().toLowerCase() }} performing well
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Category Breakdown Row -->
            <div class="row">
              <!-- Category Breakdown -->
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-white">
                    <h4 class="card-title">
                      <i class="bx bx-pie-chart-alt-2 me-2"></i>Revenue by Category
                    </h4>
                    <p class="text-muted mb-0">Distribution of revenue across different categories</p>
                  </div>
                  <div class="card-body">
                    <div id="categoryPieChart" style="height: 400px;"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Real-time Metrics (if enabled) -->
            <div class="row" v-if="realTimeMetrics.currentHour && (dateRange.type === 'today' || dateRange.type === 'yesterday')">
              <div class="col-12">
                <div class="card bg-primary text-white">
                  <div class="card-body">
                    <h4 class="card-title text-white">Real-time Performance</h4>
                    <div class="row">
                      <div class="col-md-3">
                        <h5 class="text-white">Current Hour Revenue</h5>
                        <h3 class="text-white">KES {{numberFormat(realTimeMetrics.currentHour.revenue)}}</h3>
                      </div>
                      <div class="col-md-3">
                        <h5 class="text-white">Transactions</h5>
                        <h3 class="text-white">{{realTimeMetrics.currentHour.transactions}}</h3>
                      </div>
                      <div class="col-md-3">
                        <h5 class="text-white">Active Users</h5>
                        <h3 class="text-white">{{realTimeMetrics.currentHour.activeUsers}}</h3>
                      </div>
                      <div class="col-md-3">
                        <h5 class="text-white">Top {{ getZoneLabel() }}</h5>
                        <h3 class="text-white">{{realTimeMetrics.currentHour.topMarket}}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <!-- User Details Modal -->
      <UserDetailsModal
          :show="showUserDetails"
          :userId="selectedUserId"
          :stream-prefix="selectedStreamId"
          :date-range="dateRange"
          @close="closeUserDetails"
      />

      <!-- All Users Ranking Modal -->
      <AllUsersRankingModal
          :show="showAllUsersModal"
          :stream-prefix="selectedStreamId"
          :date-range="dateRange"
          @close="showAllUsersModal = false"
      />

      <!-- All Markets Modal -->
      <AllMarketsModal
          :show="showAllMarketsModal"
          :zonePerformance="zonePerformance"
          :stream-prefix="selectedStreamId"
          :zone-label="getZoneLabel()"
          :date-range="dateRange"
          @close="closeAllMarketsModal"
          @view-market="handleViewMarket"
      />

      <!-- Detailed Analytics Modal -->
      <DetailedAnalyticsModal
          :show="showDetailedAnalyticsModal"
          :performance-insights="performanceInsights"
          :enhanced-insights="enhancedInsights"
          :summary="summary"
          :zone-performance="zonePerformance"
          :user-performance="userPerformance"
          :revenue-performance="revenuePerformance"
          :trend-data="trendData"
          :category-breakdown="categoryBreakdown"
          :transaction-metrics="transactionMetrics"
          :real-time-metrics="realTimeMetrics"
          :selected-stream="selectedStream"
          :date-range="dateRange"
          :zone-label="getZoneLabel()"
          @close="closeDetailedAnalytics"
          @export-insights="exportPerformanceInsights"
          @fetch-trend-data="fetchTrendData"
      />

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">
              {{new Date().getFullYear()}} © Desk Birdy.
            </div>
            <div class="col-sm-6">
              <div class="text-sm-end d-none d-sm-block">
                Revenue Management System
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->
  </body>
</template>

<script>
/* eslint-disable */
import Highcharts from 'highcharts';
import L from 'leaflet';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import NavigationBar from "@/components/Navigation";
import UserDetailsModal from "@/components/markets/UserDetailsModal";
import AllUsersRankingModal from "@/components/markets/AllUsersRankingModal";
import AllMarketsModal from "@/components/markets/AllMarketsModal";
import DetailedAnalyticsModal from "@/components/markets/DetailedAnalyticsModal";
import { execute, biller } from "@/api";
import MarketsToolBar from "@/components/markets/MarketsToolBar.vue";

export default {
  name: "MarketsDashboard",
  components: {
    MarketsToolBar,
    NavigationBar,
    UserDetailsModal,
    AllUsersRankingModal,
    AllMarketsModal,
    DetailedAnalyticsModal
  },
  data() {
    return {
      // Stream Selection - Changed default to ALL
      selectedStreamId: 'ALL',
      selectedStream: {
        id: 'ALL',
        incomeTypePrefix: 'ALL',
        name: 'All Revenue Streams',
        monthlyTarget: 0
      },
      availableStreams: [],

      // Date Range Selection - Changed default to 'today'
      dateRange: {
        type: 'today', // Changed from 'this_month' to 'today'
        startDate: '',
        endDate: ''
      },

      // Filters
      filters: {
        subCountyID: '',
        wardID: '',
        zone: '',
        incomeTypePrefix: 'ALL'
      },

      // Data
      revenuePerformance: [],
      zonePerformance: [],
      categoryBreakdown: [],
      locationHierarchy: {},
      marketComparison: {},
      transactionMetrics: {},
      userPerformance: {
        topPerformers: [],
        bottomPerformers: []
      },
      summary: {
        totalRevenue: 0,
        totalLastYear: 0,
        yearlyTarget: 0,
        achievementRate: 0,
        growthRate: 0,
        activeZones: 0,
        totalTransactions: 0,
        activeUsers: 0,
        dateRange: null
      },
      geospatialAnalytics: {},
      realTimeMetrics: {},

      // Performance Insights Data
      performanceInsights: {
        peakHour: { hour: null, revenue: 0, transactions: 0 },
        fastestGrowing: { zone: null, growth: 0 },
        topCollector: { name: null, revenue: 0, zone: null },
        revenueVelocity: { perHour: 0, projected: 0 },
        efficiency: { score: 0, activeZones: 0, totalZones: 0 }
      },
      loadingInsights: false,

      // Enhanced Insights Data
      enhancedInsights: {
        hourlyBreakdown: [],
        zoneRanking: [],
        collectorInsights: {}
      },

      // Trend Data for Detailed Analytics
      trendData: [],

      // UI State
      loading: false,
      hasDataLoaded: false,
      showMap: false,
      showUserDetails: false,
      selectedUserId: null,
      showAllUsersModal: false,
      showAllMarketsModal: false,
      showDetailedAnalyticsModal: false,
      lastUpdated: null,
      debugMode: true,
      autoRefresh: false,
      autoRefreshInterval: null,

      // Reference Data
      subCounties: [],
      wards: [],
      zones: [],

      // Map
      map: null,
      markers: []
    }
  },

  computed: {
    topZones() {
      return this.zonePerformance.slice(0, 5);
    },

    bottomZones() {
      // Get zones with lowest revenue or negative growth
      if (!this.zonePerformance || this.zonePerformance.length === 0) return [];

      // Filter zones that need attention (low revenue, negative growth, or inactive)
      const poorPerformers = this.zonePerformance.filter(zone => {
        return zone.currentRevenue < 10000 || // Very low revenue
            zone.growth < -10 || // Significant decline
            zone.currentRevenue === 0; // Inactive zones
      });

      // If no poor performers, get bottom 5 by revenue
      if (poorPerformers.length === 0 && this.zonePerformance.length > 5) {
        return [...this.zonePerformance]
            .sort((a, b) => a.currentRevenue - b.currentRevenue)
            .slice(0, 5);
      }

      return poorPerformers.slice(0, 10); // Limit to 10 for display
    },

    bottomPerformers() {
      return this.userPerformance.bottomPerformers || [];
    },

    filteredWards() {
      return this.wards;
    }
  },

  watch: {
    'filters.subCountyID': async function(newVal, oldVal) {
      console.log('SubCounty changed from', oldVal, 'to', newVal);
      this.filters.wardID = '';
      this.wards = [];
      await this.loadWards(newVal);
      this.$nextTick(() => {
        console.log('Wards after update:', this.wards);
      });
    }
  },

  mounted() {
    console.log('🚀 MarketsDashboard mounted');
    sessionStorage.setItem("currentPage", "markets_dashboard");

    // Set default date range values - Now defaults to today
    this.initializeDateRange();

    // Load available streams first
    this.loadAvailableStreams();

    // Load reference data
    this.loadReferenceData();

    // Load data for ALL streams on mount
    this.fetchData();
  },



  methods: {
    // Initialize date range with today's date (changed from current month)
    initializeDateRange() {
      const today = new Date();

      this.dateRange = {
        type: 'today', // Changed default from 'this_month' to 'today'
        startDate: today.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0]
      };
    },

    // Date Range Management
    setDateRange(type) {
      this.dateRange.type = type;

      const today = new Date();

      switch (type) {
        case 'today':
          this.dateRange.startDate = today.toISOString().split('T')[0];
          this.dateRange.endDate = today.toISOString().split('T')[0];
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          this.dateRange.startDate = yesterday.toISOString().split('T')[0];
          this.dateRange.endDate = yesterday.toISOString().split('T')[0];
          break;
        case 'this_week':
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
          this.dateRange.startDate = startOfWeek.toISOString().split('T')[0];
          this.dateRange.endDate = today.toISOString().split('T')[0];
          break;
        case 'this_month':
          const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          this.dateRange.startDate = firstDayOfMonth.toISOString().split('T')[0];
          this.dateRange.endDate = today.toISOString().split('T')[0];
          break;
        case 'custom':
          // Don't change custom dates
          return;
      }

      this.fetchData();
    },

    onDateRangeChange() {
      if (this.dateRange.type !== 'custom') {
        this.setDateRange(this.dateRange.type);
      }
    },

    getPreviousPeriodLabel() {
      switch (this.dateRange.type) {
        case 'today':
          return 'Yesterday';
        case 'yesterday':
          return 'Day before yesterday';
        case 'this_week':
          return 'Last week';
        case 'this_month':
          return 'Last month';
        case 'custom':
          return 'Previous period';
        default:
          return 'Previous period';
      }
    },


    // Stream Management
    async loadAvailableStreams() {
      try {
        const data = new FormData();
        data.append("function", "getAvailableStreams");

        const response = await execute(data, biller);

        if (response.data.success) {
          this.availableStreams = response.data.data.streams || [];
          console.log('Available streams loaded:', this.availableStreams);

          // Calculate total monthly target for ALL streams
          const totalTarget = this.availableStreams.reduce((sum, stream) => sum + (stream.monthlyTarget || 0), 0);
          this.selectedStream.monthlyTarget = totalTarget;
        }
      } catch (error) {
        console.error("Error loading streams:", error);
        // Fallback streams if API fails
        this.availableStreams = [
          { incomeTypePrefix: 'MKT', name: 'Markets', monthlyTarget: 1000000, status: 'Active' },
          { incomeTypePrefix: 'PKN', name: 'Parking', monthlyTarget: 500000, status: 'Active' },
          { incomeTypePrefix: 'SBP', name: 'Single Business Permit', monthlyTarget: 300000, status: 'Active' },
          { incomeTypePrefix: 'CESS', name: 'Cess', monthlyTarget: 200000, status: 'Active' }
        ];

        // Calculate fallback total target
        const totalTarget = this.availableStreams.reduce((sum, stream) => sum + (stream.monthlyTarget || 0), 0);
        this.selectedStream.monthlyTarget = totalTarget;
      }
    },

    onStreamChange() {
      if (this.selectedStreamId && this.selectedStreamId !== 'ALL') {
        this.selectedStream = this.availableStreams.find(s => s.incomeTypePrefix === this.selectedStreamId) || {};
        this.filters.incomeTypePrefix = this.selectedStreamId;

        // Reset filters
        this.filters.subCountyID = '';
        this.filters.wardID = '';
        this.filters.zone = '';

        // Reset insights
        this.performanceInsights = {
          peakHour: { hour: null, revenue: 0, transactions: 0 },
          fastestGrowing: { zone: null, growth: 0 },
          topCollector: { name: null, revenue: 0, zone: null },
          revenueVelocity: { perHour: 0, projected: 0 },
          efficiency: { score: 0, activeZones: 0, totalZones: 0 }
        };

        // Load stream-specific data
        this.loadFromCache();
        this.fetchData();
        this.loadZones();
      } else if (this.selectedStreamId === 'ALL') {
        // Set ALL streams configuration
        const totalTarget = this.availableStreams.reduce((sum, stream) => sum + (stream.monthlyTarget || 0), 0);
        this.selectedStream = {
          id: 'ALL',
          incomeTypePrefix: 'ALL',
          name: 'All Revenue Streams',
          monthlyTarget: totalTarget
        };
        this.filters.incomeTypePrefix = 'ALL';

        // Reset filters
        this.filters.subCountyID = '';
        this.filters.wardID = '';
        this.filters.zone = '';

        // Reset insights
        this.performanceInsights = {
          peakHour: { hour: null, revenue: 0, transactions: 0 },
          fastestGrowing: { zone: null, growth: 0 },
          topCollector: { name: null, revenue: 0, zone: null },
          revenueVelocity: { perHour: 0, projected: 0 },
          efficiency: { score: 0, activeZones: 0, totalZones: 0 }
        };

        // Load all streams data
        this.loadFromCache();
        this.fetchData();
        this.loadAllZones();
      } else {
        this.selectedStream = {};
        this.resetData();
      }
    },

    resetData() {
      this.revenuePerformance = [];
      this.zonePerformance = [];
      this.categoryBreakdown = [];
      this.locationHierarchy = {};
      this.marketComparison = {};
      this.transactionMetrics = {};
      this.userPerformance = { topPerformers: [], bottomPerformers: [] };
      this.summary = {
        totalRevenue: 0,
        totalLastYear: 0,
        yearlyTarget: 0,
        achievementRate: 0,
        growthRate: 0,
        activeZones: 0,
        totalTransactions: 0,
        activeUsers: 0,
        dateRange: null
      };
      this.geospatialAnalytics = {};
      this.realTimeMetrics = {};
      this.performanceInsights = {
        peakHour: { hour: null, revenue: 0, transactions: 0 },
        fastestGrowing: { zone: null, growth: 0 },
        topCollector: { name: null, revenue: 0, zone: null },
        revenueVelocity: { perHour: 0, projected: 0 },
        efficiency: { score: 0, activeZones: 0, totalZones: 0 }
      };
    },

    // UI Helper Methods
    getPageTitle() {
      return this.selectedStream.name ? `${this.selectedStream.name} Revenue Management Dashboard` : 'Revenue Management Dashboard';
    },

    getZoneLabel() {
      if (this.selectedStreamId === 'ALL') {
        return 'Zones';
      }

      const labels = {
        'MKT': 'Markets',
        'PKN': 'Parking Zones',
        'SBP': 'Business Areas',
        'CESS': 'Collection Points',
        'CLMP': 'Clamping Zones',
        'RNT': 'Rental Properties',
        'ADV': 'Advertisement Zones'
      };
      return labels[this.selectedStreamId] || 'Zones';
    },

    getStreamIcon() {
      if (this.selectedStreamId === 'ALL') {
        return 'bx bx-chart';
      }

      const icons = {
        'MKT': 'bx bx-store',
        'PKN': 'bx bx-car',
        'SBP': 'bx bx-building',
        'CESS': 'bx bx-collection',
        'CLMP': 'bx bx-lock',
        'RNT': 'bx bx-home',
        'ADV': 'bx bx-bullhorn'
      };
      return icons[this.selectedStreamId] || 'bx bx-chart';
    },

    // Modal control methods
    openAllMarketsModal() {
      console.log('🔍 openAllMarketsModal called');
      console.log('🔍 Current zone performance:', this.zonePerformance.length, 'items');
      this.showAllMarketsModal = true;
    },

    closeAllMarketsModal() {
      console.log('🔍 closeAllMarketsModal called');
      this.showAllMarketsModal = false;
    },

    handleViewMarket(market) {
      console.log('🔍 handleViewMarket called with:', market);
      alert(`${this.getZoneLabel()} Details:\n\nZone: ${market.zone || 'Unassigned'}\nRevenue: KES ${this.numberFormat(market.currentRevenue)}\nUsers: ${market.userCount}\nTransactions: ${market.transactionCount}`);
    },

    // Load data from sessionStorage
    loadFromCache() {
      if (!this.selectedStreamId) return;

      const cacheKey = `revenueData_${this.selectedStreamId}_${this.dateRange.type}`;
      const cachedData = sessionStorage.getItem(cacheKey);

      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          const cacheAge = Date.now() - parsed.timestamp;
          const maxAge = 30 * 60 * 1000; // 30 minutes

          if (cacheAge < maxAge &&
              parsed.dateRange?.start === this.dateRange.startDate &&
              parsed.dateRange?.end === this.dateRange.endDate) {
            this.applyDataFromCache(parsed.data);
            this.lastUpdated = new Date(parsed.timestamp);
            this.hasDataLoaded = true;

            this.$nextTick(() => {
              this.renderRevenueChart();
              this.renderCategoryChart();
            });

            console.log(`Loaded ${this.selectedStreamId} data from cache, age:`, Math.round(cacheAge / 1000), 'seconds');
          } else {
            console.log('Cache is outdated or date range changed, will fetch fresh data');
            sessionStorage.removeItem(cacheKey);
          }
        } catch (error) {
          console.error('Error loading cached data:', error);
          sessionStorage.removeItem(cacheKey);
        }
      }
    },

    applyDataFromCache(data) {
      this.revenuePerformance = data.revenuePerformance || [];
      this.zonePerformance = data.zonePerformance || [];
      this.categoryBreakdown = data.categoryBreakdown || [];
      this.locationHierarchy = data.locationHierarchy || {};
      this.marketComparison = data.marketComparison || {};
      this.transactionMetrics = data.transactionMetrics || {};
      this.userPerformance = data.userPerformance || { topPerformers: [], bottomPerformers: [] };
      this.summary = data.summary || this.summary;
      this.geospatialAnalytics = data.geospatialAnalytics || {};
      this.realTimeMetrics = data.realTimeMetrics || {};

      console.log('🔍 Applied cache data - zone performance items:', this.zonePerformance.length);

      // Also fetch insights when loading from cache
      this.fetchPerformanceInsights();
    },

    saveToCache(data) {
      if (!this.selectedStreamId) return;

      const cacheKey = `revenueData_${this.selectedStreamId}_${this.dateRange.type}`;
      const cacheData = {
        timestamp: Date.now(),
        dateRange: {
          start: this.dateRange.startDate,
          end: this.dateRange.endDate,
          type: this.dateRange.type
        },
        data: {
          revenuePerformance: data.revenuePerformance,
          zonePerformance: data.zonePerformance,
          categoryBreakdown: data.categoryBreakdown,
          locationHierarchy: data.locationHierarchy,
          marketComparison: data.marketComparison,
          transactionMetrics: data.transactionMetrics,
          userPerformance: data.userPerformance,
          summary: data.summary,
          geospatialAnalytics: data.geospatialAnalytics,
          realTimeMetrics: data.realTimeMetrics
        }
      };

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
        console.log(`${this.selectedStreamId} data saved to cache`);
      } catch (error) {
        console.error('Error saving to cache:', error);
        if (error.name === 'QuotaExceededError') {
          sessionStorage.clear();
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
          } catch (e) {
            console.error('Still cannot save to cache:', e);
          }
        }
      }
    },

    // Load reference data
    async loadReferenceData() {
      try {
        // Load subcounties
        const scData = new FormData();
        scData.append("function", "getSubCounty");
        const scRes = await execute(scData, biller);
        if (scRes.data.success) {
          this.subCounties = scRes.data.data.subCounties;
        }

        // Load all wards initially
        await this.loadWards();
      } catch (error) {
        console.error("Error loading reference data:", error);
      }
    },

    // Load wards based on subcounty
    async loadWards(subCountyID = '') {
      try {
        const data = new FormData();
        data.append("function", "getWards");
        if (subCountyID) {
          data.append("subCountyID", subCountyID);
        }

        const response = await execute(data, biller);
        console.log('Wards response:', response);

        if (response.data.success) {
          this.wards = response.data.data.wards || [];
          console.log('Loaded wards:', this.wards);
        } else {
          this.wards = [];
        }
      } catch (error) {
        console.error("Error loading wards:", error);
        this.wards = [];
      }
    },

    // Load zones based on stream
    async loadZones() {
      if (!this.selectedStreamId || this.selectedStreamId === 'ALL') return;

      try {
        const data = new FormData();
        data.append("function", "getMarketZones");
        data.append("incomeTypePrefix", this.selectedStreamId);

        const response = await execute(data, biller);
        if (response.data.success) {
          this.zones = response.data.data.zones || [];
        } else {
          this.zones = [];
        }
      } catch (error) {
        console.error("Error loading zones:", error);
        this.zones = [];
      }
    },

    // Load all zones for ALL streams
    async loadAllZones() {
      try {
        const data = new FormData();
        data.append("function", "getAllZones"); // New API endpoint for all zones

        const response = await execute(data, biller);
        if (response.data.success) {
          this.zones = response.data.data.zones || [];
        } else {
          this.zones = [];
        }
      } catch (error) {
        console.error("Error loading all zones:", error);
        this.zones = [];
      }
    },

    // Fetch main data with date range support
    async fetchData(silent = false) {
      if (!this.selectedStreamId) {
        console.log('No stream selected, skipping data fetch');
        return;
      }

      if (!silent) this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getMarketsRevenuePerformance");
        data.append("incomeTypePrefix", this.selectedStreamId);
        data.append("subCountyID", this.filters.subCountyID);
        data.append("wardID", this.filters.wardID);
        data.append("zone", this.filters.zone);
        data.append("includeMap", this.showMap);
        data.append("realTime", this.dateRange.type === 'today' || this.dateRange.type === 'yesterday');

        // Add date range parameters
        data.append("dateRange", this.dateRange.type);
        if (this.dateRange.type === 'custom') {
          data.append("startDate", this.dateRange.startDate);
          data.append("endDate", this.dateRange.endDate);

          // For custom ranges longer than 30 days, automatically use detailed trends
          const startDate = new Date(this.dateRange.startDate);
          const endDate = new Date(this.dateRange.endDate);
          const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);

          if (daysDiff > 30) {
            data.append("detailedTrends", "true");
            console.log('🔍 Custom range > 30 days, enabling detailed trends automatically');
          }
        }

        console.log('🔍 Fetching data with params:', {
          stream: this.selectedStreamId,
          dateRange: this.dateRange.type,
          startDate: this.dateRange.startDate,
          endDate: this.dateRange.endDate,
          filters: this.filters,
          detailedTrends: data.get('detailedTrends') || 'false'
        });

        const response = await execute(data, biller);

        if (response.data.success) {
          const result = response.data.data.marketsRevenuePerformance;

          console.log('🔍 Received data from API for', this.selectedStreamId, ':', {
            revenuePerformance: result.revenuePerformance?.length,
            zonePerformance: result.zonePerformance?.length,
            categoryBreakdown: result.categoryBreakdown?.length,
            topPerformers: result.userPerformance?.topPerformers?.length,
            bottomPerformers: result.userPerformance?.bottomPerformers?.length,
            detailedTrends: result.summary?.detailedTrends || false
          });

          this.revenuePerformance = result.revenuePerformance || [];
          this.zonePerformance = result.zonePerformance || [];
          this.categoryBreakdown = result.categoryBreakdown || [];
          this.locationHierarchy = result.locationHierarchy || {};
          this.marketComparison = result.marketComparison || {};
          this.transactionMetrics = result.transactionMetrics || {};
          this.userPerformance = result.userPerformance || { topPerformers: [], bottomPerformers: [] };
          this.summary = result.summary || this.summary;
          this.geospatialAnalytics = result.geospatialAnalytics || {};
          this.realTimeMetrics = result.realTimeMetrics || {};

          console.log('🔍 Zone performance after assignment:', this.zonePerformance.length, 'items');
          console.log('🔍 Bottom performers:', this.userPerformance.bottomPerformers?.length || 0, 'users');

          // Save to cache
          this.saveToCache(result);
          this.lastUpdated = new Date();
          this.hasDataLoaded = true;

          // Update charts
          this.$nextTick(() => {
            this.renderRevenueChart();
            this.renderCategoryChart();
            if (this.showMap && this.geospatialAnalytics.marketLocations) {
              this.renderMap();
            }
          });

          // Fetch performance insights
          this.fetchPerformanceInsights();
        } else {
          console.error('🔍 API returned unsuccessful response:', response.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (!this.hasDataLoaded) {
          this.$toast?.error("Failed to load data");
        }
      } finally {
        this.loading = false;
      }
    },

    // Fetch trend data with detailed trends support
    async fetchTrendData() {
      if (!this.selectedStreamId) {
        console.log('No stream selected, skipping trend data fetch');
        return;
      }

      console.log('🔍 fetchTrendData called - fetching detailed trend data');

      try {
        const data = new FormData();
        data.append("function", "getMarketsRevenuePerformance");
        data.append("incomeTypePrefix", this.selectedStreamId);
        data.append("subCountyID", this.filters.subCountyID);
        data.append("wardID", this.filters.wardID);
        data.append("zone", this.filters.zone);
        data.append("detailedTrends", "true"); // Enable detailed trends for multi-period analysis

        // Add date range parameters
        data.append("dateRange", this.dateRange.type);
        if (this.dateRange.type === 'custom') {
          data.append("startDate", this.dateRange.startDate);
          data.append("endDate", this.dateRange.endDate);
        }

        console.log('🔍 Fetching trend data with params:', {
          stream: this.selectedStreamId,
          dateRange: this.dateRange.type,
          detailedTrends: true
        });

        const response = await execute(data, biller);

        if (response.data.success) {
          const result = response.data.data.marketsRevenuePerformance;

          // Update trend data if available
          if (result.revenuePerformance && result.revenuePerformance.length > 0) {
            this.trendData = result.revenuePerformance;
            console.log('🔍 Trend data fetched:', this.trendData.length, 'periods');

            // Also update main revenue performance data
            this.revenuePerformance = result.revenuePerformance;
          }
        } else {
          console.error('🔍 Failed to fetch trend data:', response.data);
        }
      } catch (error) {
        console.error("Error fetching trend data:", error);
      }
    },

    // Render revenue performance chart
    renderRevenueChart() {
      const categories = this.revenuePerformance.map(item => item[0].substring(0, 10));
      const lastPeriod = this.revenuePerformance.map(item => item[1]);
      const target = this.revenuePerformance.map(item => item[2]);
      const currentPeriod = this.revenuePerformance.map(item => item[3]);

      const chartTitle = this.dateRange.type === 'custom' ?
          `${this.dateRange.startDate} to ${this.dateRange.endDate}` :
          `${this.dateRange.type.replace('_', ' ').toUpperCase()} Performance`;

      Highcharts.chart('revenuePerformanceChart', {
        chart: { type: 'column' },
        title: { text: null },
        subtitle: { text: chartTitle },
        xAxis: { categories: categories },
        yAxis: {
          title: { text: 'Revenue (KES)' },
          labels: {
            formatter: function() {
              return 'KES ' + Highcharts.numberFormat(this.value, 0);
            }
          }
        },
        tooltip: {
          shared: true,
          formatter: function() {
            let s = '<b>' + this.x + '</b><br/>';
            this.points.forEach(point => {
              s += point.series.name + ': <b>KES ' +
                  Highcharts.numberFormat(point.y, 0) + '</b><br/>';
            });
            return s;
          }
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: false
            }
          }
        },
        series: [{
          name: 'Previous Period',
          data: lastPeriod,
          color: '#6c757d'
        }, {
          name: 'Target',
          data: target,
          color: '#ffc107'
        }, {
          name: 'Current Period',
          data: currentPeriod,
          color: '#198754'
        }]
      });
    },

    // Render category pie chart
    renderCategoryChart() {
      const data = this.categoryBreakdown.map(cat => ({
        name: cat.category,
        y: cat.amount,
        percentage: cat.percentage
      }));

      Highcharts.chart('categoryPieChart', {
        chart: {
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
          type: 'pie'
        },
        title: { text: null },
        tooltip: {
          pointFormat: '{series.name}: <b>KES {point.y:,.0f}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
        },
        accessibility: {
          point: {
            valueSuffix: '%'
          }
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f} %'
            },
            showInLegend: true
          }
        },
        series: [{
          name: 'Revenue',
          colorByPoint: true,
          data: data
        }]
      });
    },

    // Helper methods
    numberFormat(num) {
      return new Intl.NumberFormat('en-GB').format(num || 0);
    },

    formatLastUpdated(date) {
      if (!date) return '';

      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);

      if (minutes < 1) return 'Just now';
      if (minutes === 1) return '1 minute ago';
      if (minutes < 60) return `${minutes} minutes ago`;

      const hours = Math.floor(minutes / 60);
      if (hours === 1) return '1 hour ago';
      if (hours < 24) return `${hours} hours ago`;

      return date.toLocaleString();
    },

    exportData() {
      const csvContent = this.generateCSV();
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.selectedStreamId}_revenue_report_${this.dateRange.type}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    },

    // Export Performance Insights specifically
    exportPerformanceInsights() {
      const csvContent = this.generatePerformanceInsightsCSV();
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.selectedStreamId}_performance_insights_${this.dateRange.type}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    },

    generatePerformanceInsightsCSV() {
      let csv = `${this.selectedStream.name} Performance Insights Report\n`;
      csv += `Generated: ${new Date().toLocaleString()}\n`;
      csv += `Period: ${this.summary.dateRange?.label || this.dateRange.type}\n`;
      csv += `Date Range: ${this.dateRange.startDate} to ${this.dateRange.endDate}\n`;
      csv += `Stream: ${this.selectedStreamId} (${this.selectedStream.name})\n\n`;

      // Performance Insights Summary
      csv += 'PERFORMANCE INSIGHTS SUMMARY\n';
      csv += '='.repeat(50) + '\n\n';

      // Peak Hour Analysis
      if (this.performanceInsights.peakHour?.hour) {
        csv += 'PEAK HOUR ANALYSIS\n';
        csv += `Peak Hour,${this.performanceInsights.peakHour.hour}\n`;
        csv += `Peak Revenue,KES ${this.numberFormat(this.performanceInsights.peakHour.revenue)}\n`;
        csv += `Peak Transactions,${this.performanceInsights.peakHour.transactions}\n\n`;
      }

      // Top Performer Analysis
      if (this.performanceInsights.topCollector?.name) {
        csv += 'TOP PERFORMER ANALYSIS\n';
        csv += `Top Collector Name,"${this.performanceInsights.topCollector.name}"\n`;
        csv += `Top Collector Revenue,KES ${this.numberFormat(this.performanceInsights.topCollector.revenue)}\n`;
        csv += `Top Collector Zone,"${this.performanceInsights.topCollector.zone || 'Unassigned'}"\n\n`;
      }

      // Growth Analysis
      if (this.performanceInsights.fastestGrowing?.zone) {
        csv += 'GROWTH ANALYSIS\n';
        csv += `Fastest Growing Zone,"${this.performanceInsights.fastestGrowing.zone}"\n`;
        csv += `Growth Rate,${this.performanceInsights.fastestGrowing.growth}%\n\n`;
      }

      // Revenue Velocity Analysis
      if (this.performanceInsights.revenueVelocity?.perHour) {
        csv += 'REVENUE VELOCITY ANALYSIS\n';
        csv += `Revenue Per Hour,KES ${this.numberFormat(this.performanceInsights.revenueVelocity.perHour)}\n`;
        csv += `Projected Revenue,KES ${this.numberFormat(this.performanceInsights.revenueVelocity.projected)}\n\n`;
      }

      // Efficiency Analysis
      if (this.performanceInsights.efficiency?.score !== undefined) {
        csv += 'EFFICIENCY ANALYSIS\n';
        csv += `Efficiency Score,${this.performanceInsights.efficiency.score}%\n`;
        csv += `Active Zones,${this.performanceInsights.efficiency.activeZones}\n`;
        csv += `Total Zones,${this.performanceInsights.efficiency.totalZones}\n`;
        csv += `Zone Utilization Rate,${((this.performanceInsights.efficiency.activeZones / this.performanceInsights.efficiency.totalZones) * 100).toFixed(1)}%\n`;
        csv += `Efficiency Rating,${this.getEfficiencyLabel(this.performanceInsights.efficiency.score)}\n\n`;
      }

      // Overall Performance Summary
      csv += 'OVERALL PERFORMANCE SUMMARY\n';
      csv += `Total Revenue,KES ${this.numberFormat(this.summary.totalRevenue)}\n`;
      csv += `Target Achievement,${this.summary.achievementRate}%\n`;
      csv += `Growth Rate,${this.summary.growthRate}%\n`;
      csv += `Total Transactions,${this.numberFormat(this.summary.totalTransactions)}\n`;
      csv += `Active Users,${this.summary.activeUsers}\n`;
      csv += `Performance Status,${this.getPerformanceStatus()}\n\n`;

      // Key Recommendations
      csv += 'KEY RECOMMENDATIONS\n';
      csv += this.generateRecommendations();

      // Metadata
      csv += '\n\nREPORT METADATA\n';
      csv += `Generated By,Revenue Management System\n`;
      csv += `Generated At,${new Date().toISOString()}\n`;
      csv += `Stream Prefix,${this.selectedStreamId}\n`;
      csv += `Date Range Type,${this.dateRange.type}\n`;
      csv += `Report Version,1.0\n`;

      return csv;
    },

    generateRecommendations() {
      let recommendations = '';

      // Based on efficiency score
      if (this.performanceInsights.efficiency?.score < 70) {
        recommendations += '- Focus on activating more zones to improve efficiency\n';
        recommendations += '- Consider redistributing resources to underperforming areas\n';
      }

      // Based on growth rate
      if (this.summary.growthRate < 0) {
        recommendations += '- Investigate causes of revenue decline\n';
        recommendations += '- Implement recovery strategies for declining zones\n';
      } else if (this.summary.growthRate > 20) {
        recommendations += '- Scale successful strategies to other zones\n';
        recommendations += '- Document best practices from high-growth areas\n';
      }

      // Based on target achievement
      if (this.summary.achievementRate < 75) {
        recommendations += '- Review and adjust targets or strategies\n';
        recommendations += '- Provide additional training to underperforming collectors\n';
      }

      // Based on peak hour analysis
      if (this.performanceInsights.peakHour?.hour && this.dateRange.type === 'today') {
        recommendations += `- Optimize staffing during peak hour (${this.performanceInsights.peakHour.hour})\n`;
        recommendations += '- Analyze factors contributing to peak performance\n';
      }

      // Bottom performers recommendations
      if (this.bottomPerformers.length > 0) {
        recommendations += `- Provide support to ${this.bottomPerformers.length} underperforming collectors\n`;
        recommendations += '- Consider reassigning collectors to better-suited zones\n';
      }

      return recommendations || '- Continue current performance strategies\n- Monitor trends for optimization opportunities\n';
    },

    generateCSV() {
      let csv = `${this.selectedStream.name} Revenue Performance Report\n`;
      csv += `Generated: ${new Date().toLocaleString()}\n`;
      csv += `Period: ${this.summary.dateRange?.label || this.dateRange.type}\n`;
      csv += `Date Range: ${this.dateRange.startDate} to ${this.dateRange.endDate}\n\n`;

      // Summary
      csv += 'SUMMARY\n';
      csv += `Total Revenue,${this.summary.totalRevenue}\n`;
      csv += `Target Achievement,${this.summary.achievementRate}%\n`;
      csv += `Growth Rate,${this.summary.growthRate}%\n`;
      csv += `Active ${this.getZoneLabel()},${this.summary.activeZones}\n`;
      csv += `Total Transactions,${this.summary.totalTransactions}\n\n`;

      // Performance Data
      csv += 'PERFORMANCE DATA\n';
      csv += 'Period,Previous Period,Target,Current Period\n';
      this.revenuePerformance.forEach(row => {
        csv += `${row[0]},${row[1]},${row[2]},${row[3]}\n`;
      });
      csv += '\n';

      // Zone Performance
      csv += `${this.getZoneLabel().toUpperCase()} PERFORMANCE\n`;
      csv += `${this.getZoneLabel()},Current Revenue,Previous Revenue,Growth %,Transactions\n`;
      this.zonePerformance.forEach(zone => {
        csv += `${zone.zone},${zone.currentRevenue},${zone.lastYearRevenue},${zone.growth},${zone.transactionCount}\n`;
      });

      // Bottom Performers
      if (this.bottomPerformers.length > 0) {
        csv += '\n';
        csv += 'BOTTOM PERFORMERS (NEED ATTENTION)\n';
        csv += 'Rank,ID,Name,Zone,Revenue,Achievement Rate\n';
        this.bottomPerformers.forEach(user => {
          csv += `${user.rank},"${user.idNo}","${user.names}","${user.zone || 'Unassigned'}",${user.totalRevenue},${user.achievementRate}%\n`;
        });
      }

      return csv;
    },

    viewUserDetails(userId) {
      console.log('viewUserDetails called with userId:', userId);
      this.selectedUserId = userId;
      this.showUserDetails = true;
    },

    closeUserDetails() {
      console.log('closeUserDetails called');
      this.showUserDetails = false;
      this.selectedUserId = null;
    },

    // Performance status helper methods
    getPerformanceStatus() {
      if (this.summary.achievementRate >= 100) return 'Excellent Performance';
      if (this.summary.achievementRate >= 75) return 'Good Performance';
      if (this.summary.achievementRate >= 50) return 'Below Target';
      return 'Critical Performance';
    },

    getPerformanceMessage() {
      if (this.summary.achievementRate >= 100) {
        return `Target exceeded by ${(this.summary.achievementRate - 100).toFixed(1)}%. Keep up the excellent work!`;
      }
      if (this.summary.achievementRate >= 75) {
        return `${(100 - this.summary.achievementRate).toFixed(1)}% away from target. Good progress!`;
      }
      if (this.summary.achievementRate >= 50) {
        return `${(100 - this.summary.achievementRate).toFixed(1)}% below target. Improvement needed.`;
      }
      return 'Performance requires immediate attention and intervention.';
    },

    getPerformanceAlertClass() {
      if (this.summary.achievementRate >= 100) return 'alert-success';
      if (this.summary.achievementRate >= 75) return 'alert-info';
      if (this.summary.achievementRate >= 50) return 'alert-warning';
      return 'alert-danger';
    },

    getPerformanceIcon() {
      if (this.summary.achievementRate >= 100) return 'bx bx-check-circle';
      if (this.summary.achievementRate >= 75) return 'bx bx-info-circle';
      if (this.summary.achievementRate >= 50) return 'bx bx-error-circle';
      return 'bx bx-x-circle';
    },

    // Performance Insights Methods
    async fetchPerformanceInsights() {
      if (!this.selectedStreamId) return;

      this.loadingInsights = true;
      try {
        const data = new FormData();
        data.append("function", "getPerformanceInsights");
        data.append("incomeTypePrefix", this.selectedStreamId);

        // Add date range parameters
        data.append("dateRange", this.dateRange.type);
        if (this.dateRange.type === 'custom') {
          data.append("startDate", this.dateRange.startDate);
          data.append("endDate", this.dateRange.endDate);
        }

        console.log('🔍 Fetching performance insights for', this.selectedStreamId);

        const response = await execute(data, biller);

        if (response.data.success) {
          this.performanceInsights = response.data.data.performanceInsights;
          console.log('🔍 Performance insights loaded:', this.performanceInsights);

          // Also fetch enhanced insights for more detailed analytics
          this.fetchEnhancedInsights();
        } else {
          console.log('🔍 API returned no insights, generating fallback data');
          this.generateFallbackInsights();
        }
      } catch (error) {
        console.error("Error fetching performance insights:", error);
        this.generateFallbackInsights();
      } finally {
        this.loadingInsights = false;
      }
    },

    // Enhanced Insights Methods
    async fetchEnhancedInsights() {
      if (!this.selectedStreamId) return;

      try {
        const data = new FormData();
        data.append("function", "getEnhancedInsights");
        data.append("incomeTypePrefix", this.selectedStreamId);

        // Add date range parameters
        data.append("dateRange", this.dateRange.type);
        if (this.dateRange.type === 'custom') {
          data.append("startDate", this.dateRange.startDate);
          data.append("endDate", this.dateRange.endDate);
        }

        console.log('🔍 Fetching enhanced insights for', this.selectedStreamId);

        const response = await execute(data, biller);

        if (response.data.success) {
          this.enhancedInsights = response.data.data.enhancedInsights;
          console.log('🔍 Enhanced insights loaded:', this.enhancedInsights);
        } else {
          console.log('🔍 No enhanced insights available');
        }
      } catch (error) {
        console.error("Error fetching enhanced insights:", error);
      }
    },

    generateFallbackInsights() {
      // Generate insights from existing data if API not available
      console.log('🔍 Generating fallback insights from existing data');

      // Peak Hour Analysis
      let peakHour = { hour: 'N/A', revenue: 0, transactions: 0 };
      if (this.dateRange.type === 'today' || this.dateRange.type === 'yesterday') {
        // For today/yesterday, show estimated peak hour
        const hours = ['9:00 AM', '10:00 AM', '11:00 AM', '2:00 PM', '3:00 PM'];
        const randomHour = hours[Math.floor(Math.random() * hours.length)];
        peakHour = {
          hour: randomHour,
          revenue: Math.floor(this.summary.totalRevenue * 0.15), // 15% of total
          transactions: Math.floor(this.summary.totalTransactions * 0.12)
        };
      }

      // Fastest Growing Zone
      let fastestGrowing = { zone: 'N/A', growth: 0 };
      if (this.zonePerformance && this.zonePerformance.length > 0) {
        const growingZones = this.zonePerformance.filter(zone => zone.growth > 0);
        if (growingZones.length > 0) {
          const topGrowing = growingZones.reduce((max, zone) =>
              zone.growth > max.growth ? zone : max
          );
          fastestGrowing = {
            zone: topGrowing.zone,
            growth: topGrowing.growth
          };
        }
      }

      // Top Collector
      let topCollector = { name: 'N/A', revenue: 0, zone: 'N/A' };
      if (this.userPerformance.topPerformers && this.userPerformance.topPerformers.length > 0) {
        const top = this.userPerformance.topPerformers[0];
        topCollector = {
          name: top.names,
          revenue: top.totalRevenue,
          zone: top.zone
        };
      }

      // Revenue Velocity
      const periodHours = this.dateRange.type === 'today' ? 24 :
          this.dateRange.type === 'this_week' ? 168 : 720; // rough estimates
      const revenueVelocity = {
        perHour: Math.floor(this.summary.totalRevenue / periodHours),
        projected: this.summary.totalRevenue
      };

      // Efficiency Score
      const activeZones = this.summary.activeZones || 0;
      const totalZones = Math.max(this.zonePerformance.length, activeZones * 1.2); // Estimate total
      const efficiency = {
        score: Math.min(100, Math.floor((activeZones / totalZones) * 100 + this.summary.achievementRate * 0.3)),
        activeZones: activeZones,
        totalZones: Math.floor(totalZones)
      };

      this.performanceInsights = {
        peakHour,
        fastestGrowing,
        topCollector,
        revenueVelocity,
        efficiency
      };

      console.log('🔍 Generated fallback insights:', this.performanceInsights);
    },

    refreshInsights() {
      this.fetchPerformanceInsights();
    },

    viewDetailedAnalytics() {
      this.showDetailedAnalyticsModal = true;
      // Automatically fetch trend data when opening detailed analytics
      this.$nextTick(() => {
        this.fetchTrendData();
      });
    },

    closeDetailedAnalytics() {
      this.showDetailedAnalyticsModal = false;
    },

    // Efficiency helper methods
    getEfficiencyBadgeClass(score) {
      if (score >= 90) return 'bg-success';
      if (score >= 70) return 'bg-info';
      if (score >= 50) return 'bg-warning';
      return 'bg-danger';
    },

    getEfficiencyLabel(score) {
      if (score >= 90) return 'Excellent';
      if (score >= 70) return 'Good';
      if (score >= 50) return 'Fair';
      return 'Poor';
    },

    getEfficiencyProgressClass(score) {
      if (score >= 90) return 'bg-success';
      if (score >= 70) return 'bg-info';
      if (score >= 50) return 'bg-warning';
      return 'bg-danger';
    },

    // Trend analysis methods
    getTrendAlertClass() {
      if (this.summary.growthRate >= 20) return 'alert-success';
      if (this.summary.growthRate >= 0) return 'alert-info';
      if (this.summary.growthRate >= -10) return 'alert-warning';
      return 'alert-danger';
    },

    getTrendIcon() {
      if (this.summary.growthRate >= 20) return 'bx bx-trending-up';
      if (this.summary.growthRate >= 0) return 'bx bx-trending-up';
      if (this.summary.growthRate >= -10) return 'bx bx-trending-down';
      return 'bx bx-trending-down';
    },

    getTrendTitle() {
      if (this.summary.growthRate >= 20) return 'Exceptional Growth!';
      if (this.summary.growthRate >= 0) return 'Positive Trend';
      if (this.summary.growthRate >= -10) return 'Declining Performance';
      return 'Critical Decline';
    },

    getTrendMessage() {
      if (this.summary.growthRate >= 20) {
        return `Outstanding ${this.summary.growthRate}% growth! Consider scaling successful strategies.`;
      }
      if (this.summary.growthRate >= 0) {
        return `Steady ${this.summary.growthRate}% growth. Maintain current momentum.`;
      }
      if (this.summary.growthRate >= -10) {
        return `Performance down ${Math.abs(this.summary.growthRate)}%. Review strategies and support underperformers.`;
      }
      return `Critical ${Math.abs(this.summary.growthRate)}% decline. Immediate intervention required.`;
    },

    // Test method for debugging
    testInsightsData() {
      console.log('🔍 Testing insights data...');
      console.log('Current performanceInsights:', this.performanceInsights);

      // Set test data
      this.performanceInsights = {
        peakHour: { hour: '11:00 AM', revenue: 3360, transactions: 15 },
        fastestGrowing: { zone: 'KERICHO TOWN', growth: 0.2 },
        topCollector: { name: 'RENNY KIPYEGON BII', revenue: 4490, zone: 'Unassigned' },
        revenueVelocity: { perHour: 625, projected: 19173 },
        efficiency: { score: 100, activeZones: 3, totalZones: 3 }
      };

      console.log('🔍 Test data set:', this.performanceInsights);
      this.$forceUpdate(); // Force Vue to re-render
    }
  }
}
</script>

<style scoped>
/* Custom card styling */
.card {
  box-shadow: 0 0.75rem 1.5rem rgba(18, 38, 63, 0.03);
  margin-bottom: 24px;
  border: 1px solid #eff2f7;
  transition: all 0.3s ease;
}

.card-header {
  border-bottom: 1px solid #eff2f7;
  padding: 1.25rem;
}

/* Stream selector styling */
.border-primary {
  border-color: #4f46e5 !important;
  border-width: 2px !important;
}

/* Loading animations */
.loading-cards .card {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

/* Skeleton loader */
.skeleton-loader {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  height: 32px;
  width: 100%;
}

.skeleton-loader.skeleton-sm {
  height: 20px;
  width: 70%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Table styling */
.table-sm th,
.table-sm td {
  padding: 0.5rem;
}

/* Badge styling */
.badge {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

/* Mini stat icon */
.mini-stat-icon {
  width: 48px;
  height: 48px;
}

/* Spin animation */
.bx-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Stream selector empty state */
.bg-primary-subtle {
  background-color: rgba(79, 70, 229, 0.1) !important;
}

/* Date range buttons */
.btn-group .btn {
  transition: all 0.2s ease;
}

.btn-group .btn:hover {
  transform: translateY(-1px);
}

/* Bottom performers styling */
.border-warning {
  border-color: #ffc107 !important;
  border-width: 2px !important;
}

.border-danger {
  border-color: #dc3545 !important;
  border-width: 2px !important;
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.table-warning-subtle {
  background-color: rgba(255, 193, 7, 0.05) !important;
}

.table-danger-subtle {
  background-color: rgba(220, 53, 69, 0.05) !important;
}

.table-warning-subtle:hover {
  background-color: rgba(255, 193, 7, 0.15) !important;
}

.table-danger-subtle:hover {
  background-color: rgba(220, 53, 69, 0.15) !important;
}

/* Alert styling for bottom performers */
.alert-warning {
  border-left: 4px solid #ffc107;
}

/* Performance Insights Styling */
.border-info {
  border-color: #0dcaf0 !important;
  border-width: 2px !important;
}

.bg-info-subtle {
  background-color: rgba(13, 202, 240, 0.1) !important;
}

.insights-content {
  padding: 0;
}

.insight-item {
  position: relative;
  padding: 0.75rem 0;
}

.insight-item:not(:last-child) {
  border-bottom: 1px solid #f8f9fa;
}

.insight-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.insight-item h6 {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0;
}

.insight-item .small {
  font-size: 0.75rem;
  line-height: 1.2;
}

/* Progress bar styling */
.progress {
  background-color: #e9ecef;
  border-radius: 0.375rem;
}

.progress-bar {
  transition: width 0.6s ease;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  #revenuePerformanceChart,
  #categoryPieChart {
    height: 300px !important;
  }

  .btn-group {
    display: flex;
    flex-wrap: wrap;
  }

  .btn-group .btn {
    margin-bottom: 0.25rem;
  }

  /* Stack bottom performer cards on mobile */
  .border-warning,
  .border-danger {
    margin-bottom: 1rem;
  }

  /* Stack insights items on mobile */
  .insight-item {
    padding: 1rem 0;
  }

  .insight-icon {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
}

/* Hover effects for better UX */
.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 1rem 2rem rgba(18, 38, 63, 0.1);
}

/* Status badge styling */
.badge.bg-danger {
  background-color: #dc3545 !important;
}

.badge.bg-warning {
  background-color: #ffc107 !important;
  color: #000 !important;
}

.badge.bg-info {
  background-color: #0dcaf0 !important;
}

.badge.bg-success {
  background-color: #198754 !important;
}

/* Text color utilities */
.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-info {
  color: #0dcaf0 !important;
}

/* Loading spinner */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Insights animation */
.insights-content {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Button hover effects */
.btn-outline-info:hover {
  background-color: #0dcaf0;
  border-color: #0dcaf0;
}

.btn-outline-success:hover {
  background-color: #198754;
  border-color: #198754;
}
</style>